import DashboardLayout from "@/pages/Layout/DashboardLayout.vue";

const routes = [
  {
    path: "/",
    component: DashboardLayout,
    redirect: "/dashboard",
    children: [
      {
        // The single landing page for everyone. Dashboard.vue renders the
        // staff cards for an admin and <ClientDashboard> for a client user.
        path: "dashboard",
        name: "Dashboard",
        component: () => import("@/pages/Dashboard.vue"),
        meta: { requiresClientAccess: true },
      },
      {
        // Every user type edits their own details here (PATCH /user is
        // self-service; editing someone else is /users/:id, admin-only).
        path: "user",
        name: "User Profile",
        component: () => import("@/pages/UserProfile.vue"),
        meta: { requiresClientAccess: true },
      },
      {
        // A client user's streams, one row each, with recording controls.
        path: "/my/streams",
        name: "Client Streams",
        component: () => import("@/pages/client/ClientStreams.vue"),
        meta: { requiresClientAccess: true },
      },
      {
        // Read-only stream detail for a client user. Deliberately a separate
        // page from /stream/:id (the 9k-line operator console), not a
        // permission-gated version of it.
        path: "/my/stream/:stream_id",
        name: "Client Stream",
        component: () => import("@/pages/client/ClientStream.vue"),
        meta: { requiresClientAccess: true },
      },
      {
        path: "users",
        name: "Users",
        component: () => import("@/pages/Users.vue"),
        meta: { requiresAdmin: true },
      },
      {
        path: "/users/:user_id",
        name: "User Detail",
        component: () => import("@/pages/UserDetail.vue"),
        meta: { requiresAdmin: true },
      },
      {
        path: "audit-log",
        name: "Audit Log",
        component: () => import("@/pages/AuditLog.vue"),
        meta: { requiresAdmin: true },
      },
      {
        // Old user editor was replaced by /users (Kratos auth refactor)
        path: "findedituser",
        redirect: "/users",
      },
      {
        path: "shorturl",
        name: "URL Shortener",
        component: () => import("@/pages/ShortUrl.vue"),
        meta: { requiresAdmin: true },
      },
      {
        path: "streams",
        name: "Streams",
        component: () => import("@/pages/Streams.vue"),
        meta: { requiresAdmin: true },
      },
      {
        path: "encode_cluster",
        name: "Encode Cluster",
        component: () => import("@/pages/EncodeCluster.vue"),
        meta: { requiresAdmin: true },
      },
      {
        path: "encode_cluster_config",
        name: "Encode Cluster Config",
        component: () => import("@/pages/ClusterConfig.vue"),
        props: { clusterKind: "encode" },
        meta: { requiresAdmin: true },
      },
      {
        path: "streaming_cluster",
        name: "Input Cluster",
        component: () => import("@/pages/StreamingCluster.vue"),
        meta: { requiresAdmin: true },
      },
      {
        path: "streaming_cluster_config",
        name: "Input Cluster Config",
        component: () => import("@/pages/ClusterConfig.vue"),
        props: { clusterKind: "streaming" },
        meta: { requiresAdmin: true },
      },
      {
        path: "api_cluster",
        name: "Audience/API Cluster",
        component: () => import("@/pages/APICluster.vue"),
        meta: { requiresAdmin: true },
      },
      {
        path: "api_cluster_config",
        name: "Audience/API Cluster Config",
        component: () => import("@/pages/ClusterConfig.vue"),
        props: { clusterKind: "api" },
        meta: { requiresAdmin: true },
      },
      {
        path: "db_cluster",
        name: "DB Cluster",
        component: () => import("@/pages/DbCluster.vue"),
        meta: { requiresAdmin: true },
      },
      {
        path: "db_cluster_config",
        name: "DB Cluster Config",
        component: () => import("@/pages/ClusterConfig.vue"),
        props: { clusterKind: "db" },
        meta: { requiresAdmin: true },
      },
      {
        path: "infra_cluster_config",
        name: "Infra Config",
        component: () => import("@/pages/ClusterConfig.vue"),
        props: { clusterKind: "infra" },
        meta: { requiresAdmin: true },
      },
      {
        path: "platform_status",
        name: "Platform Status",
        component: () => import("@/pages/PlatformStatus.vue"),
        meta: { requiresAdmin: true },
      },
      {
        // Desired pin vs running version for every component; reads
        // /admin/infra/rollout (computed on read, no new state).
        path: "deployments",
        name: "Deployments",
        component: () => import("@/pages/Deployments.vue"),
        meta: { requiresAdmin: true },
      },
      {
        // Viewer telemetry (sp2_player QoE beacons): platform-wide view and
        // the per-session drill-down. Both read /admin/audience/*.
        path: "audience",
        name: "Audience",
        component: () => import("@/pages/Audience.vue"),
        meta: { requiresAdmin: true },
      },
      {
        path: "/audience/viewer/:vsid",
        name: "Audience Viewer",
        component: () => import("@/pages/AudienceViewer.vue"),
        meta: { requiresAdmin: true },
      },
      {
        path: "quickshare",
        name: "Quick Share",
        component: () => import("@/pages/QuickShare.vue"),
        meta: { requiresAdmin: true },
      },
      {
        // Staff, plus client users whose grant carries stream_config_access
        // for the stream's account. The page cannot know the account before
        // it loads the stream, so the guard admits every client user and
        // isi_data answers 404 for anyone without the flag (client mode then
        // shows the load error).
        path: "/stream/:stream_id",
        name: "Stream",
        component: () => import("@/pages/Stream.vue"),
        meta: { requiresClientAccess: true },
      },
      {
        // Stream2 was merged into Stream.vue in commit 840c066. Keep this
        // redirect so users with /stream2/... bookmarked or open in a tab
        // land on the consolidated page instead of a blank route.
        path: "/stream2/:stream_id",
        redirect: (to) => ({
          name: "Stream",
          params: { stream_id: to.params.stream_id },
          query: to.query,
        }),
      },
      {
        // Client users reach this for their own streams too (the page hides
        // the operator-only controls; isi_data scopes the sample reads).
        path: "/stream/:stream_id/diagnostics",
        name: "Stream Diagnostics",
        component: () => import("@/pages/StreamDiagnostics.vue"),
        meta: { requiresClientAccess: true },
      },
      {
        // Same audience as the Stream page: staff, plus client users whose
        // grant carries stream_config_access. Both pages show a not-available
        // error when isi_data refuses the stream.
        path: "/stream/:stream_id/activity",
        name: "Stream Activity",
        component: () => import("@/pages/StreamActivity.vue"),
        meta: { requiresClientAccess: true },
      },
      {
        path: "/broadcast-history/:stream_id",
        name: "Broadcast History Explorer",
        component: () => import("@/pages/BroadcastHistoryExplorer.vue"),
        meta: { requiresClientAccess: true },
      },
      {
        path: "platform-settings",
        name: "Platform Settings",
        component: () => import("@/pages/PlatformSettings.vue"),
        meta: { requiresAdmin: true },
      },
      {
        path: "videos",
        name: "Videos",
        component: () => import("@/pages/Videos.vue"),
        meta: { requiresAdmin: true },
      },
      {
        // File-access routes: admins plus client users (client_access
        // grants from GET /user). The guard in main.js enforces it.
        path: "files",
        name: "File Browser",
        component: () => import("@/pages/FileBrowser.vue"),
        meta: { requiresFileAccess: true },
      },
      {
        // One video in full, addressed by path. An SP2 asset at that path
        // canonicalizes to /vod_asset/:asset_id (same component).
        path: "files/details",
        name: "File Details",
        component: () => import("@/pages/VideoDetails.vue"),
        meta: { requiresFileAccess: true },
      },
      {
        path: "files/edit",
        name: "Video Editor",
        component: () => import("@/pages/VideoEditor.vue"),
        meta: { requiresFileAccess: true },
      },
      {
        path: "files/vtt",
        name: "VTT Editor",
        component: () => import("@/pages/VttEditor.vue"),
        meta: { requiresFileAccess: true },
      },
      {
        // Encoder pages: admins plus client users whose grants cover the
        // encoder's client account (server-enforced; the guard only gates
        // reachability).
        path: "encoders",
        name: "Encoders",
        component: () => import("@/pages/Encoders.vue"),
        meta: { requiresEncoderAccess: true },
      },
      {
        path: "/encoder/:device_id",
        name: "Encoder",
        component: () => import("@/pages/Encoder.vue"),
        meta: { requiresEncoderAccess: true },
      },
      {
        path: "/vod_asset/new",
        name: "VodAssetCreate",
        component: () => import("@/pages/VodAssetCreate.vue"),
        meta: { requiresAdmin: true },
      },
      {
        // The same page as files/details, addressed by asset id. Client
        // users reach it too; the page hides the staff-only sections and
        // isi_data scopes every read and write to the asset's account.
        path: "/vod_asset/:asset_id",
        name: "VodAsset",
        component: () => import("@/pages/VideoDetails.vue"),
        meta: { requiresFileAccess: true },
      },
      {
        path: "client-accounts",
        name: "Client Accounts",
        component: () => import("@/pages/ClientAccounts.vue"),
        meta: { requiresAdmin: true },
      },
      {
        // Staff manage any account here; a client user reaches their own,
        // read-only below client_admin. The server decides what they may see
        // and write (isi_data client_accounts.js clientAccountView /
        // CLIENT_ADMIN_WRITABLE).
        path: "/client_account/:id",
        name: "Client Account",
        component: () => import("@/pages/ClientAccount.vue"),
        meta: { requiresClientAccess: true },
      },
      {
        path: "release-notes",
        name: "Release Notes",
        component: () => import("@/pages/ReleaseNotes.vue"),
        meta: { requiresAdmin: true },
      },
      {
        // The AI assistant and documentation pages are reachable by URL only
        // until they are reworked; the sidebar no longer links them.
        path: "admin-docs/assistant",
        name: "AI Assistant",
        component: () => import("@/pages/AdminAssistant.vue"),
        meta: { requiresAdmin: true },
      },
      {
        path: "admin-docs/docs",
        name: "Documentation",
        component: () => import("@/pages/AdminDocumentation.vue"),
        meta: { requiresAdmin: true },
      },
    ],
  },
];

export default routes;
